import { fx_crearBaseLocal, reqToPromise, txDone } from "../../../../utils/idbUtils";

const DB_NAME = "grpc_monitoreo_broca";
const DB_VERSION = 3;
const STORE_MONITOREOS = "monitoreos";
const STORE_SYNC = "sync";

/** Item mínimo que necesita el merge de delta (mismo contrato que
 *  `GrpcLoteItem`/`GrpcAplicacionItem`: watermark por finca, no uno global). */
export type GrpcMonitoreoBrocaItem = {
  id: string;
  eliminado: boolean;
  ultimaActualizacion: bigint;
};

/** IndexedDB **nueva y separada** para los monitoreos de broca que trae
 *  `MonitoreoBrocaGrpcService` (ver CLAUDE.md del Server, sección migración a
 *  gRPC-Web) — mismo patrón que `GrpcLoteDatabase`/`GrpcAplicacionDatabase`:
 *  delta real por `fincaId`. */
class GrpcMonitoreoBrocaDatabase {
  /** Conexión a la base local con el ayudante común, que al cambiar de
   *  versión conserva los datos (ver `fx_crearBaseLocal`). La versión 3
   *  (2026-09-30) se subió a pedido del usuario para una limpieza única al
   *  estrenar el ayudante: un navegador con una versión anterior vacía la
   *  base una sola vez y la vuelve a llenar desde el servidor. */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      { nombre: STORE_MONITOREOS, clave: "id", indices: ["fincaId"] },
      { nombre: STORE_SYNC, clave: "fincaId" },
    ],
    { vaciarAntesDe: 3 },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  async getUltimaSincronizacionAsync(fincaId: string): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ fincaId: string; ultimaSincronizacion: string } | undefined>(
      store.get(fincaId),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }

  async putDeltaAsync<T extends GrpcMonitoreoBrocaItem>(
    fincaId: string,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([STORE_MONITOREOS, STORE_SYNC], "readwrite");
    const store = tx.objectStore(STORE_MONITOREOS);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ fincaId, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  async getListByFincaIdAsync<T>(fincaId: string): Promise<T[]> {
    const db = await this.open();
    const index = db
      .transaction(STORE_MONITOREOS, "readonly")
      .objectStore(STORE_MONITOREOS)
      .index("fincaId");
    return reqToPromise<T[]>(index.getAll(fincaId));
  }

  /** Actualiza (o inserta) un solo monitoreo sin tocar el resto (alta/edición
   *  puntual). */
  async putOneAsync<T extends GrpcMonitoreoBrocaItem>(item: T): Promise<void> {
    const db = await this.open();
    const tx = db.transaction(STORE_MONITOREOS, "readwrite");
    tx.objectStore(STORE_MONITOREOS).put(item);
    await txDone(tx);
  }
}

/** Singleton de la base de monitoreos de broca por gRPC-Web (con delta por
 *  finca). */
export const grpcMonitoreoBrocaDatabase = new GrpcMonitoreoBrocaDatabase();
